// src/components/CommandPalette/useWorkspaceItems.js
//
// Proyectos, agentes y workflows de la cuenta activa, aplanados para buscarlos
// por nombre.
//
// La carga es perezosa: no se pide nada hasta que alguien abre el palette por
// primera vez. Es lo que evita añadirle llamadas a cada pantalla por una función
// que se usa a ráfagas. Una vez cargado se cachea mientras dure la sesión y la
// cuenta no cambie.
//
// La búsqueda es en cliente sobre las listas ya traídas. Con los volúmenes de
// hoy sobra; cuando deje de sobrar se cambia por un endpoint sin tocar la UI,
// porque el palette solo consume `items`.
import { useEffect, useMemo, useRef, useState } from "react";
import { useOrganizations, useProjects } from "../../hooks/useOrganizations";
import AssistantService from "../../services/assistant.service";
import WorkflowService from "../../services/workflow.service";

// Mismo tope que usa Home al aplanar workflows por proyecto.
const MAX_PROJECTS_TO_SCAN = 12;

export const ITEM_TYPES = {
  project: { key: "project", label: "Projects", i18nKey: "palette.groupProjects" },
  agent: { key: "agent", label: "Agents", i18nKey: "palette.groupAgents" },
  workflow: { key: "workflow", label: "Workflows", i18nKey: "palette.groupWorkflows" },
};

export function useWorkspaceItems(enabled) {
  const { currentAccount } = useOrganizations();
  const { projects } = useProjects();
  const accountId = currentAccount?.id;
  const accountReady = !!accountId && !currentAccount?._restored;

  const [assistants, setAssistants] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  // Una vez abierto el palette seguimos manteniendo los datos frescos, pero no
  // volvemos a "no cargado" al cerrarlo.
  const [primed, setPrimed] = useState(false);
  const fetchedFor = useRef(null);

  useEffect(() => {
    if (enabled) setPrimed(true);
  }, [enabled]);

  const projectList = useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);
  const projectIdsKey = projectList.map((p) => p.id).join(",");

  useEffect(() => {
    if (!primed || !accountReady) return undefined;
    const signature = `${accountId}|${projectIdsKey}`;
    if (fetchedFor.current === signature) return undefined;

    let cancelled = false;
    setLoading(true);

    const agentsP = AssistantService.listAssistants({ accountId, limit: 100 })
      .then((res) => (res?.success && Array.isArray(res.data) ? res.data : []))
      .catch(() => []);

    // El endpoint de workflows exige project_id, así que hay que ir proyecto a
    // proyecto y unir — igual que en Home.
    const workflowsP = projectList.length
      ? Promise.all(
          projectList.slice(0, MAX_PROJECTS_TO_SCAN).map((p) =>
            WorkflowService.listWorkflows({
              account_id: accountId,
              project_id: p.id,
              limit: 50,
              include_definition: false,
            })
              .then((r) => (r?.success ? (r.data?.workflows || []).map((w) => ({ ...w, project_id: p.id })) : []))
              .catch(() => [])
          )
        ).then((perProject) => perProject.flat())
      : Promise.resolve([]);

    Promise.all([agentsP, workflowsP]).then(([a, w]) => {
      if (cancelled) return;
      // La firma se marca al terminar, no al empezar. Marcarla antes dejaba el
      // hook envenenado: si el efecto se cancelaba a medias, la siguiente
      // ejecución con la misma firma salia por el guard y los datos no llegaban
      // nunca — el palette se quedaba vacio para siempre.
      fetchedFor.current = signature;
      setAssistants(a);
      setWorkflows(w);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // projectList se lee dentro pero no va en las dependencias a proposito: su
    // identidad cambia en cada render del contexto y relanzaria la carga sin
    // que haya cambiado nada. projectIdsKey es el contenido, que es lo que
    // importa. Mismo patron que HomeDashboard con sus propias listas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primed, accountReady, accountId, projectIdsKey]);

  const items = useMemo(() => {
    const out = [];

    projectList.forEach((p) => {
      const id = p.project_id || p.id;
      out.push({
        id: `project:${id}`,
        type: "project",
        name: p.name || "Untitled project",
        sub: p.description || "",
        path: `/dashboard?view=Projects&projectId=${id}`,
      });
    });

    // Un agente cuyo proyecto no está en la cuenta activa abriría una pantalla
    // vacía: mejor no ofrecerlo. Mismo criterio que "Continue working" en Home.
    const projectIds = new Set(projectList.map((p) => p.id));
    assistants.forEach((a) => {
      const pid = a.projectId || a.project_id;
      if (!projectIds.has(pid)) return;
      out.push({
        id: `agent:${a.id || a.assistant_id || a.name}`,
        type: "agent",
        name: a.name || "Untitled agent",
        sub: a.description || "",
        path: `/dashboard?view=Projects&projectId=${pid}&tab=assistants`,
      });
    });

    workflows.forEach((w) => {
      const pid = w.project_id || w.projectId;
      out.push({
        id: `workflow:${w.id || w.workflow_id || w.name}`,
        type: "workflow",
        name: w.name || "Untitled workflow",
        sub: w.status ? String(w.status) : "",
        path: pid
          ? `/dashboard?view=Projects&projectId=${pid}&tab=workflows`
          : "/dashboard?view=Projects",
      });
    });

    return out;
  }, [projectList, assistants, workflows]);

  return { items, loading };
}
